import { create } from 'zustand';
import { endpoints } from '../api/api';

export const useRelationshipsStore = create((set, get) => ({
    // All relationships
    relationships: [],
    loading: false,
    error: null,

    // Filters
    filters: {
        search: '',
        status: 'all', // all, vip, at-risk, active, inactive
        sort: 'strength', // strength, lastContact, alphabetical
    },

    // Selected person
    selectedPerson: null,
    personLoading: false,

    // Actions
    setFilters: (filters) => set((state) => ({
        filters: { ...state.filters, ...filters }
    })),

    setSelectedPerson: (person) => set({ selectedPerson: person }),
    clearSelectedPerson: () => set({ selectedPerson: null }),

    // Fetch all relationships
    fetchRelationships: async () => {
        set({ loading: true, error: null });

        try {
            const { filters } = get();
            const response = await endpoints.relationships.getAll(filters);

            // Transform Python backend response to frontend format
            const transformedData = (response.data || []).map(contact => ({
                id: contact.person_id || contact.id,
                name: contact.name || 'Unknown',
                email: contact.email || '',
                company: contact.organization || '',
                role: contact.title || '',
                isVip: contact.is_vip || false,
                score: contact.relationship_score || contact.score || 50,
                sentiment: contact.sentiment || 'neutral',
                lastInteraction: contact.last_interaction || contact.last_contact || new Date().toISOString(),
                platforms: contact.platforms || {},
                trend: contact.score < 50 ? 'down' : 'stable',
            }));

            set({ relationships: transformedData, loading: false });
        } catch (error) {
            console.error('Failed to fetch relationships:', error);
            // Use mock data for demo
            set({
                relationships: [
                    {
                        id: '1',
                        name: 'Sarah Chen',
                        email: 'sarah@example.com',
                        avatar: null,
                        company: 'TechCorp',
                        role: 'Product Manager',
                        isVip: true,
                        score: 95,
                        sentiment: 'positive',
                        lastInteraction: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
                        platforms: {
                            email: { count: 47, lastAt: new Date().toISOString() },
                            slack: { count: 123, lastAt: new Date().toISOString() },
                        },
                        trend: 'up',
                    },
                    {
                        id: '2',
                        name: 'Jake Miller',
                        email: 'jake@example.com',
                        avatar: null,
                        company: 'DesignStudio',
                        role: 'Lead Designer',
                        isVip: true,
                        score: 88,
                        sentiment: 'positive',
                        lastInteraction: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
                        platforms: {
                            email: { count: 32, lastAt: new Date().toISOString() },
                            slack: { count: 89, lastAt: new Date().toISOString() },
                        },
                        trend: 'stable',
                    },
                    {
                        id: '3',
                        name: 'Alex Kim',
                        email: 'alex@example.com',
                        avatar: null,
                        company: 'LegalFirm',
                        role: 'Legal Counsel',
                        isVip: false,
                        score: 72,
                        sentiment: 'neutral',
                        lastInteraction: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
                        platforms: {
                            email: { count: 15, lastAt: new Date().toISOString() },
                            slack: { count: 0, lastAt: null },
                        },
                        trend: 'down',
                    },
                    {
                        id: '4',
                        name: 'Mark Johnson',
                        email: 'mark@example.com',
                        avatar: null,
                        company: 'Investors Inc',
                        role: 'Partner',
                        isVip: true,
                        score: 65,
                        sentiment: 'neutral',
                        lastInteraction: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
                        platforms: {
                            email: { count: 8, lastAt: new Date().toISOString() },
                            slack: { count: 0, lastAt: null },
                        },
                        trend: 'down',
                    },
                    {
                        id: '5',
                        name: 'Lisa Wang',
                        email: 'lisa@example.com',
                        avatar: null,
                        company: 'Marketing Agency',
                        role: 'Creative Director',
                        isVip: false,
                        score: 45,
                        sentiment: 'negative',
                        lastInteraction: new Date(Date.now() - 1000 * 60 * 60 * 168).toISOString(),
                        platforms: {
                            email: { count: 5, lastAt: new Date().toISOString() },
                            slack: { count: 2, lastAt: null },
                        },
                        trend: 'down',
                    },
                ],
                loading: false,
            });
        }
    },

    // Fetch single person details
    fetchPerson: async (id) => {
        set({ personLoading: true });

        try {
            const response = await endpoints.relationships.getById(id);
            set({ selectedPerson: response.data, personLoading: false });
        } catch (error) {
            console.error('Failed to fetch person:', error);
            // Find from existing relationships
            const { relationships } = get();
            const person = relationships.find(r => r.id === id);
            if (person) {
                set({
                    selectedPerson: {
                        ...person,
                        timeline: [
                            { type: 'email', subject: 'Project update', date: new Date().toISOString() },
                            { type: 'meeting', title: 'Weekly sync', date: new Date(Date.now() - 86400000).toISOString() },
                            { type: 'slack', message: 'Quick question about the designs', date: new Date(Date.now() - 172800000).toISOString() },
                        ],
                    },
                    personLoading: false,
                });
            }
        }
    },

    // Toggle VIP status
    toggleVip: async (id) => {
        try {
            await endpoints.relationships.toggleVip(id);
            set((state) => ({
                relationships: state.relationships.map(r =>
                    r.id === id ? { ...r, isVip: !r.isVip } : r
                ),
                selectedPerson: state.selectedPerson?.id === id
                    ? { ...state.selectedPerson, isVip: !state.selectedPerson.isVip }
                    : state.selectedPerson,
            }));
        } catch (error) {
            console.error('Failed to toggle VIP:', error);
            // Still toggle in UI for demo
            set((state) => ({
                relationships: state.relationships.map(r =>
                    r.id === id ? { ...r, isVip: !r.isVip } : r
                ),
            }));
        }
    },

    // Get filtered and sorted relationships
    getFilteredRelationships: () => {
        const { relationships, filters } = get();

        let filtered = [...relationships];

        // Apply search
        if (filters.search) {
            const search = filters.search.toLowerCase();
            filtered = filtered.filter(r =>
                r.name.toLowerCase().includes(search) ||
                r.email.toLowerCase().includes(search) ||
                r.company?.toLowerCase().includes(search)
            );
        }

        // Apply status filter
        switch (filters.status) {
            case 'vip':
                filtered = filtered.filter(r => r.isVip);
                break;
            case 'at-risk':
                filtered = filtered.filter(r => r.score < 50 || r.trend === 'down');
                break;
            case 'active':
                filtered = filtered.filter(r => r.score >= 70);
                break;
            case 'inactive':
                filtered = filtered.filter(r => {
                    const lastContact = new Date(r.lastInteraction);
                    const daysSince = (Date.now() - lastContact.getTime()) / (1000 * 60 * 60 * 24);
                    return daysSince > 7;
                });
                break;
            default:
                break;
        }

        // Apply sort
        switch (filters.sort) {
            case 'strength':
                filtered.sort((a, b) => b.score - a.score);
                break;
            case 'lastContact':
                filtered.sort((a, b) => new Date(b.lastInteraction) - new Date(a.lastInteraction));
                break;
            case 'alphabetical':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            default:
                break;
        }

        return filtered;
    },
}));

export default useRelationshipsStore;
